import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Text, theme, Icon, Button, Select, message } from 'components';
import {
  ParsedSupraLockboxJsonType,
  updateOwnerOrAssignee,
} from 'api/SupraLockboxController';
import { debounce } from 'lodash';
import styled from 'styled-components';
import Axios from 'axios';
import { AuthContext } from 'contexts';
import { Modal } from 'react-bootstrap';
import { Loading } from '@app/react-common';
import { buildLockboxAssignConfirmMessage, featureEnabled } from 'utils';
import { OwnerAndAssigneeType } from '@bb/domain/dist/types/SupraLockbox';
import SearchLabel from './SearchLabel';

type SearchBarProps = {
  disabled?: boolean;
  isMlsUser: boolean;
  lockbox: ParsedSupraLockboxJsonType;
  fetchLockboxData?: any;
};

type AssigneeValueType = {
  id: string;
  type: string;
  label: JSX.Element;
};

const AssigneeSearchBar: React.FC<SearchBarProps> = ({
  isMlsUser,
  lockbox,
  disabled,
  fetchLockboxData,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const initialValue: AssigneeValueType | null = useMemo(() => {
    if (!lockbox || !lockbox.assignee || !lockbox.parsedAssignee) return null;

    setData([{ id: lockbox.assignee, ...lockbox.parsedAssignee }]);
    return {
      id: lockbox.parsedAssignee.id ?? lockbox.assignee,
      type: lockbox.parsedAssignee.type,
      label: SearchLabel(lockbox.parsedAssignee, isMlsUser),
    };
  }, [lockbox, isMlsUser]);

  const [value, setValue] = useState<AssigneeValueType | null>(initialValue);
  const [confirmUnassignVisible, setConfirmUnassignVisible] = useState(false);
  const [confirmReassignVisible, setConfirmReassignVisible] = useState(false);

  const [assigneeType, setAssigneeType] = useState<
    'user' | 'branch' | 'office' | ''
  >('');

  const prevAssigneeValue = useRef<AssigneeValueType | null>(null);
  const newAssigneeValue = useRef('');

  // Clear search results when assigneeType changes
  useEffect(() => {
    if (featureEnabled('improvedLockboxAssignmentFeature')) setData([]);
  }, [assigneeType]);

  useEffect(() => {
    setValue(initialValue); // Update the value when the lockbox prop changes
  }, [initialValue]);

  // If search value is cleared or lockbox owner changes, reset select dropdown
  useEffect(() => {
    if (!value) {
      setAssigneeType('');
    }
  }, [value, lockbox.ownerType, lockbox.owner]);

  const { auth: user } = useContext(AuthContext);

  const unassignLockbox = async () => {
    try {
      await updateOwnerOrAssignee({
        serialNumber: lockbox.serialNumber,
        assigneeId: prevAssigneeValue.current?.id,
        assigneeType: prevAssigneeValue.current?.type,
        updateType: 'unassign',
      });
      if (lockbox._id) {
        await fetchLockboxData(lockbox._id);
      }
      message.success('Assignee unassigned');
      prevAssigneeValue.current = null;
    } catch (err) {
      setValue(prevAssigneeValue.current); // Restore the previous value when unassign fails
      message.error(err?.response?.data?.message || err.message);
    }
  };

  const reassignLockbox = async (newValue: string) => {
    try {
      const selectedValue = data.find((d: any) => d.id === newValue);
      const parsedValue = SearchLabel(selectedValue, isMlsUser);
      setValue({ id: newValue, label: parsedValue, type: selectedValue.type });
      await updateOwnerOrAssignee({
        serialNumber: lockbox.serialNumber,
        assigneeId: newValue,
        assigneeType: selectedValue.type,
        updateType: 'assign',
      });
      message.success('Assignee added');
      if (lockbox._id) {
        await fetchLockboxData(lockbox._id);
      }
    } catch (err) {
      let errorMsg: string = err?.response?.data?.message || err.message;
      if (errorMsg.includes('not authorized to')) {
        // Only show last error
        const errorMsgs = errorMsg.split('BrokerBayForbiddenError: ');
        errorMsg = errorMsgs[errorMsgs.length - 1];
      }
      message.error(errorMsg);
      if (!prevAssigneeValue?.current) {
        setValue(null);
        setData([]);
      } else {
        setData([{ id: lockbox.assignee, ...lockbox.parsedAssignee }]);
        setValue(prevAssigneeValue.current);
        prevAssigneeValue.current = null;
      }
    }
  };

  const handleConfirmUnassignOk = async () => {
    setLoading(true);
    await unassignLockbox();
    setConfirmUnassignVisible(false);
    setLoading(false);
  };

  const handleConfirmUnassignCancel = () => {
    setConfirmUnassignVisible(false);
    setValue(prevAssigneeValue.current); // Restore the previous value when the user cancels the confirmation dialog
    prevAssigneeValue.current = null;
  };

  const handleConfirmReassignOk = async () => {
    setLoading(true);
    await reassignLockbox(newAssigneeValue.current);
    setConfirmReassignVisible(false);
    setLoading(false);
  };

  const handleConfirmReassignCancel = () => {
    setValue(prevAssigneeValue.current); // Restore the previous value when the user cancels the confirmation dialog
    prevAssigneeValue.current = null;
    setConfirmReassignVisible(false);
  };

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      if (
        !searchTerm ||
        (featureEnabled('improvedLockboxAssignmentFeature') && !assigneeType)
      )
        return;
      setLoading(true);
      setData([]);

      let queryString = `serialNumber=${lockbox.serialNumber}&searchTerm=${searchTerm}&includeUserMlsId=1&includeOfficeId=1&includeOfficeName=1&size=10&page=0`;
      if (featureEnabled('improvedLockboxAssignmentFeature')) {
        queryString += `&type=${assigneeType}`;
      }

      const {
        data: { searchResult },
      } = await Axios.get(`/lockbox/owner_and_assignee?${queryString}`);
      setLoading(false);
      setData(searchResult);
    },
    [lockbox.serialNumber, assigneeType],
  );

  const debouncedError = useMemo(
    () => debounce((msg: string) => message.error(msg), 500),
    [],
  );

  const debounceHandleSearch = useMemo(
    () => debounce(handleSearch, 300),
    [handleSearch],
  );

  const handleChange = async (newValue: string) => {
    if (!newValue) {
      setValue(null);
      return;
    }

    // Confirm for reassignment
    if (value) {
      prevAssigneeValue.current = value; // Save the previous value before showing the confirmation dialog
      newAssigneeValue.current = newValue;
      setConfirmReassignVisible(true);
      return;
    }

    await reassignLockbox(newValue);
  };

  const handleClear = () => {
    if (value) {
      prevAssigneeValue.current = value; // Save the previous value before showing the confirmation dialog
      setConfirmUnassignVisible(true);
    } else {
      setValue(null);
    }
  };

  return (
    <>
      <StyledSelectContainer>
        {featureEnabled('improvedLockboxAssignmentFeature') && (
          <Select
            value={assigneeType}
            onChange={v => setAssigneeType(v)}
            placeholder="Select type"
            style={{ width: 100 }}
            size="large"
            data-cy="assignee-type-select"
            options={[
              { value: 'user', label: 'User' },
              // Show office option if owner is MLS/BDR
              ...(lockbox.ownerType === 'mls' || lockbox.ownerType === 'board'
                ? [{ value: 'office', label: 'Office' }]
                : []),
            ]}
          />
        )}
        {!featureEnabled('improvedLockboxAssignmentFeature') && (
          <StyledSearchIcon type="search" width="16px" />
        )}
        <StyledSelect
          virtual={false}
          showSearch
          value={value?.id}
          placeholder={
            featureEnabled('improvedLockboxAssignmentFeature')
              ? 'Search'
              : 'Add an assignee'
          }
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          notFoundContent={loading ? <Loading /> : null}
          onSearch={searchTerm => {
            if (
              featureEnabled('improvedLockboxAssignmentFeature') &&
              !assigneeType
            ) {
              debouncedError(
                'Please select a type before searching for an assignee.',
              );
              return;
            }
            debounceHandleSearch(searchTerm);
          }}
          onChange={handleChange}
          onClear={handleClear}
          disabled={disabled}
          size="large"
          allowClear
          options={(data || []).map((d: any) => ({
            value: d?.id,
            label: SearchLabel(d, isMlsUser),
          }))}
        />
      </StyledSelectContainer>
      {/* Confirm unassignment modal */}
      <Modal
        onHide={handleConfirmUnassignCancel}
        show={confirmUnassignVisible}
        style={{ borderRadius: '5px' }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Unassign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {buildLockboxAssignConfirmMessage({
            action: 'Unassigning',
            ownerType: lockbox?.ownerType as OwnerAndAssigneeType,
            assigneeType: lockbox?.assigneeType,
            isAssignee: true,
            listingAddress: lockbox.listing?.line1
              ? `${lockbox.listing.line1} ${lockbox.listing?.line2 ?? ''}`.trim()
              : undefined,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleConfirmUnassignCancel}
            style={{ marginRight: '10px' }}
          >
            Close
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmUnassignOk}
            loading={loading}
            style={{ color: '#fff' }}
          >
            Unassign
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Confirm reassignment modal */}
      <Modal onHide={handleConfirmReassignCancel} show={confirmReassignVisible}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Reassign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {buildLockboxAssignConfirmMessage({
            action: 'Reassigning',
            ownerType: lockbox?.ownerType as OwnerAndAssigneeType,
            assigneeType: lockbox?.assigneeType,
            isAssignee: true,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleConfirmReassignCancel}
            style={{ marginRight: '10px' }}
          >
            Close
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmReassignOk}
            loading={loading}
            style={{ color: '#fff' }}
          >
            Reassign
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const StyledSelectContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledSearchIcon = styled(Icon)`
  position: absolute;
  left: 10px;
  top: 12px;
  z-index: 1;
  color: ${theme.gray8};
`;

const StyledSelect = styled(Select)`
  width: 425px;
  min-width: 280px;
  display: flex;
  position: relative;
  align-items: center;
  .ant-select-selection-item {
    padding: 0 20px !important;
  }
  .ant-select-selection-search {
    padding: 0px 25px !important;
  }
  .ant-select-selection-placeholder {
    padding: 0px 25px !important;
  }
`;

export default AssigneeSearchBar;
